import { useMemo } from 'react';
import { clsx } from 'clsx';

interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
  fillOpacity?: number;
  strokeWidth?: number;
  showArea?: boolean;
  className?: string;
  animated?: boolean;
}

export function Sparkline({
  data,
  width = 80,
  height = 24,
  color = '#f59e0b',
  fillOpacity = 0.1,
  strokeWidth = 1.5,
  showArea = true,
  className,
  animated = true,
}: SparklineProps) {
  const pathData = useMemo(() => {
    if (!data || data.length === 0) return { line: '', area: '' };

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const padding = 2;
    const effectiveHeight = height - padding * 2;
    const effectiveWidth = width - padding * 2;

    const points = data.map((value, index) => {
      const x = padding + (index / (data.length - 1)) * effectiveWidth;
      const y = padding + effectiveHeight - ((value - min) / range) * effectiveHeight;
      return { x, y };
    });

    // Create line path
    const line = points
      .map((point, index) => {
        if (index === 0) return `M ${point.x} ${point.y}`;
        return `L ${point.x} ${point.y}`;
      })
      .join(' ');

    // Create area path (closed shape)
    const area = `${line} L ${points[points.length - 1].x} ${height - padding} L ${padding} ${height - padding} Z`;

    return { line, area };
  }, [data, width, height]);

  if (!data || data.length < 2) {
    return (
      <div
        className={clsx('flex items-center justify-center text-xs text-gray-400', className)}
        style={{ width, height }}
      >
        No data
      </div>
    );
  }

  return (
    <svg
      width={width}
      height={height}
      className={className}
      viewBox={`0 0 ${width} ${height}`}
    >
      {showArea && (
        <path
          d={pathData.area}
          fill={color}
          fillOpacity={fillOpacity}
          className={animated ? 'animate-in fade-in duration-500' : ''}
        />
      )}
      <path
        d={pathData.line}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={animated ? 'animate-in fade-in duration-500' : ''}
      />
    </svg>
  );
}

// Sparkline with trend indicator
interface SparklineTrendProps extends SparklineProps {
  showTrend?: boolean;
  trendThreshold?: number;
}

export function SparklineTrend({
  data,
  showTrend = true,
  trendThreshold = 5,
  ...props
}: SparklineTrendProps) {
  const trend = useMemo(() => {
    if (!data || data.length < 2) return null;

    const first = data[0];
    const last = data[data.length - 1];
    const percentChange = first !== 0 ? ((last - first) / first) * 100 : 0;

    if (Math.abs(percentChange) < trendThreshold) {
      return { direction: 'neutral' as const, value: percentChange };
    }
    return {
      direction: percentChange > 0 ? 'up' as const : 'down' as const,
      value: percentChange,
    };
  }, [data, trendThreshold]);

  const color = useMemo(() => {
    if (!trend) return '#9ca3af';
    if (trend.direction === 'up') return '#22c55e';
    if (trend.direction === 'down') return '#ef4444';
    return '#9ca3af';
  }, [trend]);

  return (
    <div className="flex items-center gap-2">
      <Sparkline data={data} color={color} {...props} />
      {showTrend && trend && (
        <span
          className={clsx(
            'text-xs font-medium',
            trend.direction === 'up' && 'text-green-600',
            trend.direction === 'down' && 'text-red-600',
            trend.direction === 'neutral' && 'text-gray-500'
          )}
        >
          {trend.direction === 'up' && '↑'}
          {trend.direction === 'down' && '↓'}
          {trend.direction === 'neutral' && '→'}
          {Math.abs(trend.value).toFixed(1)}%
        </span>
      )}
    </div>
  );
}

// Mini bar chart for simple comparisons
interface MiniBarChartProps {
  data: Array<{ label: string; value: number; color?: string }>;
  width?: number;
  height?: number;
  barWidth?: number;
  gap?: number;
  className?: string;
}

export function MiniBarChart({
  data,
  width = 80,
  height = 24,
  barWidth = 8,
  gap = 2,
  className,
}: MiniBarChartProps) {
  const maxValue = Math.max(...data.map(d => d.value), 1);
  const totalWidth = data.length * barWidth + (data.length - 1) * gap;
  const startX = (width - totalWidth) / 2;

  return (
    <svg width={width} height={height} className={className}>
      {data.map((item, index) => {
        const barHeight = (item.value / maxValue) * (height - 4);
        const x = startX + index * (barWidth + gap);
        const y = height - barHeight - 2;

        return (
          <rect
            key={item.label}
            x={x}
            y={y}
            width={barWidth}
            height={barHeight}
            rx={2}
            fill={item.color || '#f59e0b'}
            className="animate-in slide-in-from-bottom duration-300"
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <title>{`${item.label}: ${item.value}`}</title>
          </rect>
        );
      })}
    </svg>
  );
}
